import { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import { getQuestionAnchor, mapApiDataToTabs } from './Help.helpers';
import { GET_FAQ_CATEGORIES } from '@/api/queries/faq.queries';
import { GetFaqCategoriesResponse } from '@/api/types/faq.types';
import { Tab } from '@/components/Tabs/Tabs.types';
import { ROUTES } from '@/constants/routes';
import { useUrlAndHashFromPath } from '@/hooks/useUrlAndHashFromPath';
import { logError } from '@/utils/log-error';
import { usePageTopControlsStore } from '@/store/usePageTopControlsStore';

export function useHelp() {
  const router = useRouter();
  const { url, hash } = useUrlAndHashFromPath();
  const isBrowser = typeof window !== 'undefined';
  const topControlsHeight = usePageTopControlsStore(
    state => state.topControlsHeight
  );
  const { data, loading, error, refetch } = useQuery<GetFaqCategoriesResponse>(
    GET_FAQ_CATEGORIES,
    {
      skip: !isBrowser || !router.isReady,
      ssr: false,
      fetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
    }
  );
  const categoryPath = router.query.category;
  const slug = Array.isArray(categoryPath)
    ? categoryPath.join('/')
    : categoryPath ?? '';
  const categories = error ? [] : data?.faqCategories ?? [];
  const category = categories.find(item => item.slug === slug);
  const isLoading = !isBrowser || !router.isReady || loading;
  const questions = (category?.questions ?? []).map(item => {
    const anchor = getQuestionAnchor(item.id);
    const isOpen = hash === anchor;

    return {
      ...item,
      anchor,
      isOpen,
      onToggle: () => {
        router
          .push(isOpen ? url : `${url}#${anchor}`, undefined, {
            shallow: true,
            scroll: false,
          })
          .catch(navigationError => logError(String(navigationError)));
      },
    };
  });

  useEffect(() => {
    if (isLoading || !category || !hash) return;

    const element = document.getElementById(`faq-question-${hash}`);
    if (!element) return;

    window.scrollTo({
      top:
        element.getBoundingClientRect().top +
        window.scrollY -
        topControlsHeight,
      behavior: 'smooth',
    });
  }, [isLoading, category, hash, topControlsHeight]);

  function handleCategorySelect(tab: Tab) {
    router
      .push(`${ROUTES.help}/${tab.id}`, undefined, { shallow: true })
      .catch(navigationError => logError(String(navigationError)));
  }

  function handleRetry() {
    refetch().catch(requestError => logError(String(requestError)));
  }

  return {
    title: category?.name ?? '',
    tabs: mapApiDataToTabs(categories, slug),
    questions,
    isLoading,
    isError: !!error,
    isNotFound: !isLoading && !error && !category,
    handleCategorySelect,
    handleRetry,
  };
}
